import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {useActionsStore} from '~/store/rie/actions';
import {useInventarisationStore} from '~/store/rie/inventarisation';
import {useRisksStore} from '~/store/rie/risks';
import {getProgressPercentage} from '~/lib/inventorisationHelper';
import type {DashboardOverviewData} from '~/lib/types/dashboardTypes';

export const useDashboardStore = defineStore('dashboard', () => {

	const activeRie = ref<string>();

	const inventarisationStore = useInventarisationStore();
	const riskStore = useRisksStore();
	const actionStore = useActionsStore();

	const clearData = () => {
		inventarisationStore.clearData();
		riskStore.clearData();
		actionStore.clearData();
	};

	const refreshData = (rieId: string) => {
		if (rieId !== activeRie.value) {
			clearData();
		}
		activeRie.value = rieId;

		inventarisationStore.fetchProgress(rieId);
		riskStore.fetchRiskOverview(rieId);
		actionStore.fetchActionOverview(rieId);
	};

	const inventarisationData = computed(() => {
		let totalQuestions = 0;
		let totalAnswered = 0;
		let nextQuestion: { questionKey: string, topicKey: string } | undefined;

		inventarisationStore.progress?.themes.forEach((theme) => {
			totalQuestions += theme.totalQuestions;
			totalAnswered += theme.answeredQuestions;

			if (!nextQuestion && typeof theme.nextQuestion !== 'undefined') {
				nextQuestion = theme.nextQuestion;
			}
		});

		return {
			percentage: getProgressPercentage(totalAnswered, totalQuestions),
			nextQuestion
		};
	});

	const overviewData = computed(() => {
		return {
			inventarisation: {
				loading: inventarisationStore.progressLoading,
				data: inventarisationData.value
			},
			risks: {
				loading: riskStore.loading,
				data: {
					noActions: riskStore.relevantRisks.filter((risk) => risk.numberOfActions === 0).length,
					total: riskStore.relevantRisks.length
				}
			},
			actions: {
				loading: actionStore.loading,
				data: {
					total: actionStore.actions.length,
					openActions: actionStore.actions.filter((action) => action.status === 'OPEN')
				}
			}
		} satisfies DashboardOverviewData;
	});

	return {
		refreshData,
		overviewData
	};

});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useDashboardStore, import.meta.hot));
}
